/**
 * Campaigns Module Types — Frontend
 *
 * (Internamente todavía se llaman "Objective" en el código por compatibilidad
 * con el backend / API endpoints. En la UI se muestran como "Campañas".)
 *
 * Una Campaña agrupa: efeméride / contexto + segmento + plantilla WhatsApp +
 * cadencia, en algo que el marketer activa con un click.
 *
 * Los 9 presets representan las efemérides + recurrencias personales más
 * relevantes para un restaurante en LatAm:
 *   - 7 efemérides de fecha fija
 *   - 2 recurrentes personales (cumple + aniversario como cliente)
 */

export type ObjectivePreset =
  // ─── Efemérides (fecha fija) ───
  | "valentines"             // 14 feb — Día de los Enamorados
  | "mothers_day"            // 3er domingo de octubre (AR) — Día de la Madre
  | "fathers_day"            // 3er domingo de junio — Día del Padre
  | "friends_day"            // 20 julio — Día del Amigo
  | "kids_day"               // 3er domingo de agosto — Día del Niño
  | "workers_day"            // 1 mayo — Día del Trabajador
  | "christmas_newyear"      // 22-dic a 1-ene — Navidad y Año Nuevo
  | "birthday"               // Recurrente — Feliz cumpleaños del cliente
  | "customer_anniversary"   // Recurrente — Aniversario como cliente
  // ─── Comportamentales / lifecycle (trigger-based) ───
  | "cart_abandon"           // Carrito abandonado: 2hs sin completar
  | "winback_vip"            // Reactivación de cliente VIP con LTV alto
  | "purchase_frequency"     // Empujar a 1-3 pedidos a comprar más seguido
  | "aov_increase"           // Sugerir combos a clientes con ticket bajo
  | "welcome_first"          // Bienvenida primera compra
  | "winback_general"        // Despertar inactivos 60+ días
  | "cross_sell"             // Cross-sell 3 días post-pedido
  | "reorder_reminder"       // Próximo pedido estimado por IA
  | "review_request"         // Pedir review 2 días post-entrega
  // ─── Personalizada (sin blueprint) ───
  | "custom";                // Campaña personalizada — el user define todo desde cero

/**
 * Status lifecycle (ampliado para soportar los 3 tipos de campaña).
 *
 *   draft           → marca editando (Tipo 3)
 *   pending_review  → enviada a la IA, esperando veredicto (Tipo 3)
 *   needs_changes   → la IA observó, requiere iteración (Tipo 3)
 *   suggested       → Deenex la propuso, marca decide (Tipo 2)
 *   dismissed       → marca descartó la sugerencia (Tipo 2)
 *   active          → corriendo (cualquier tipo)
 *   paused          → pausada por marca o por la IA
 *   archived        → terminada o vieja
 */
export type ObjectiveStatus =
  | "draft"
  | "pending_review"
  | "needs_changes"
  | "suggested"
  | "dismissed"
  | "active"
  | "paused"
  | "archived";

export type ObjectiveSetupMode = "manual" | "auto";
export type CadenceKind = "one_shot" | "recurring";

/**
 * Origen de la campaña — discrimina las 3 maneras de tener una campaña en
 * la plataforma:
 *
 *   automatic → Deenex la prendió como always-on (carrito, inactivos, etc.)
 *               La marca puede pausar y editar copy, pero no borrar el trigger.
 *
 *   suggested → Deenex la propone (efeméride, oportunidad detectada, rescate).
 *               La marca acepta, edita o descarta.
 *
 *   custom    → La marca la creó desde cero o desde un preset.
 *               Pasa por revisión iterativa de la IA antes de activarse.
 */
export type CampaignOrigin = "automatic" | "suggested" | "custom";

/* ─── Multi-canal + triggers ───
 *
 * Una campaña tiene N canales activos. Cada canal tiene su propio
 * contenido (email tiene subject, push tiene title corto, WA es plano).
 * Una campaña tiene un grupo de triggers conectados por lógica AND/OR
 * top-level (sin nesting en iteración 1).
 */

export type CampaignChannel = "email" | "whatsapp" | "push";

export interface EmailContent {
  subject: string;
  preheader?: string;
  body: string;
}

export interface WhatsAppContent {
  text: string;
  mediaUrl?: string;
}

export interface PushContent {
  title: string;
  body: string;
  imageUrl?: string;
  deepLink?: string;
}

export interface CampaignContent {
  email?: EmailContent;
  whatsapp?: WhatsAppContent;
  push?: PushContent;
}

/** Triggers — discriminated union por type */

export type TriggerType = "event" | "time" | "whatsapp_message";

/** Trigger por evento — la marca elige uno del catálogo */
export interface EventTrigger {
  id: string;
  type: "event";
  /** ID del evento del catálogo (ver event-catalog.ts) */
  eventKey: string;
  /** Parámetros opcionales del evento, ej: { hoursSince: 2, minValue: 5000 } */
  params?: Record<string, string | number>;
}

/** Trigger por tiempo — fecha fija o recurrencia */
export interface TimeTrigger {
  id: string;
  type: "time";
  /** Subtipo: "fixed" (una fecha) | "recurring" (cron-like) | "relative" (X días después de Y) */
  mode: "fixed" | "recurring" | "relative";
  /** Solo en mode=fixed: ISO date */
  scheduledFor?: string;
  /** Solo en mode=recurring: días de semana (1-7) */
  daysOfWeek?: number[];
  /** Solo en mode=recurring: hora local "HH:MM" */
  timeOfDay?: string;
  /** Solo en mode=relative: días post-evento */
  daysOffset?: number;
  /** Solo en mode=relative: eventKey de referencia */
  relativeTo?: string;
}

/** Trigger por mensaje de WhatsApp — categoría + opcional keyword extra */
export type WhatsAppMessageCategory =
  | "complaint"
  | "inquiry"
  | "cancellation"
  | "thanks"
  | "info_request"
  | "menu_request"
  | "reservation_request"
  | "other";

export interface WhatsAppMessageTrigger {
  id: string;
  type: "whatsapp_message";
  /** La IA categoriza el mensaje entrante en una de estas */
  category: WhatsAppMessageCategory;
  /** Keywords opcionales que también disparan (override de la categoría) */
  keywords?: string[];
}

export type Trigger = EventTrigger | TimeTrigger | WhatsAppMessageTrigger;

export interface TriggerGroup {
  /** Top-level: cumplir TODOS (AND) o CUALQUIERA (OR) de los triggers de la lista */
  logic: "AND" | "OR";
  items: Trigger[];
}

/* ─── AI review (Tipo 3 mainly, también para auto-pausa de Tipo 1) ─── */

export type AIObservationCategory =
  | "tone_mismatch"
  | "frequency_conflict"
  | "forbidden_word"
  | "campaign_clash"
  | "spam_risk"
  | "timing_issue"
  | "copy_improvement";

export interface AIObservation {
  category: AIObservationCategory;
  severity: "info" | "warning" | "critical";
  message: string;
  /** Sugerencia accionable que la marca puede aplicar */
  suggestion?: string;
}

export interface AIReview {
  iteration: number;
  lastReviewedAt?: string;
  verdict?: "approved" | "needs_changes";
  observations: AIObservation[];
  approvedAt?: string;
  /** Mensaje firmado por la IA (ej "Listo, sale mañana 10:00") */
  approverNote?: string;
}

export interface CadenceWindow {
  start: string;
  end: string;
}

export interface ObjectiveCadence {
  kind: CadenceKind;
  sendWindow: CadenceWindow;
  daysOfWeek?: number[];
  scheduledFor?: string;
  triggerCondition?: string;
}

export interface ObjectiveMetrics {
  runsCount: number;
  peopleReached: number;
  conversions: number;
  revenueGeneratedUSD: number;
  lastRunAt?: string;
}

/**
 * Métricas que el usuario quiere "levantar" / mejorar con la campaña.
 * Solo aplica a campañas custom — los presets ya tienen métricas implícitas.
 */
export type CampaignTargetMetric =
  | "revenue"            // 💰 Ingresos totales
  | "orders_count"       // 🛒 Cantidad de pedidos
  | "purchase_frequency" // 📈 Frecuencia de compra
  | "avg_ticket"         // 💸 Ticket promedio
  | "retention"          // 🔁 Retención
  | "new_customers"      // 👥 Nuevos clientes
  | "reviews"            // ⭐ Reviews / NPS
  | "reservations";      // 📅 Reservas

export interface Objective {
  id: string;
  brandId: string;
  name: string;
  emoji: string;
  preset: ObjectivePreset;
  segmentId?: string;
  templateId?: string;
  setupMode?: ObjectiveSetupMode;
  cadence: ObjectiveCadence;
  status: ObjectiveStatus;
  metrics: ObjectiveMetrics;
  /** Solo en preset === "custom": qué quiere lograr el usuario, texto libre */
  customGoal?: string;
  /** Solo en preset === "custom": KPIs que el usuario quiere mejorar */
  customMetrics?: CampaignTargetMetric[];

  /* ─── Modelo de 3 tipos ─── */
  /** Quién originó la campaña (automatic / suggested / custom) */
  origin: CampaignOrigin;
  /** Si origin === 'automatic', marca las always-on (pausables pero no borrables) */
  isAlwaysOn?: boolean;
  /** Para origin === 'suggested' — por qué la IA la propuso */
  suggestionReason?: string;
  /** Vencimiento implícito de una sugerencia (efeméride pasa, oportunidad caduca) */
  suggestionExpiresAt?: string;
  /** Si la marca descartó la sugerencia */
  dismissReason?: string;
  /** Trail de revisiones de la IA (custom + auto-pausa) */
  aiReview?: AIReview;
  /** Copy editable del mensaje (mock legacy — para mostrar en cards y review.
   *  Modelo nuevo usa `content.whatsapp.text` etc.) */
  messagePreview?: string;

  /* ─── Multi-canal + triggers (nuevo modelo) ─── */
  /** Canales activos. Cada uno tiene su contenido en `content.<channel>` */
  channels?: CampaignChannel[];
  /** Contenido por canal — solo se renderiza el de canales activos */
  content?: CampaignContent;
  /** Triggers que disparan la campaña — agrupados por AND/OR top-level */
  triggers?: TriggerGroup;

  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/* ─── DTOs ─── */

export interface CreateObjectiveDTO {
  brandId: string;
  preset: ObjectivePreset;
  name?: string;
  emoji?: string;
  /** Texto libre — solo en preset custom */
  customGoal?: string;
  /** KPIs a mejorar — solo en preset custom */
  customMetrics?: CampaignTargetMetric[];
}

/** Metadata UI de las métricas seleccionables (label + emoji + descripción corta) */
export const TARGET_METRICS: {
  key: CampaignTargetMetric;
  emoji: string;
  label: string;
  description: string;
}[] = [
  { key: "revenue", emoji: "💰", label: "Ingresos totales", description: "Aumentar la facturación total" },
  { key: "orders_count", emoji: "🛒", label: "Cantidad de pedidos", description: "Volumen de transacciones" },
  { key: "purchase_frequency", emoji: "📈", label: "Frecuencia de compra", description: "Pedidos por cliente / mes" },
  { key: "avg_ticket", emoji: "💸", label: "Ticket promedio", description: "Valor promedio por pedido" },
  { key: "retention", emoji: "🔁", label: "Retención", description: "% clientes que vuelven" },
  { key: "new_customers", emoji: "👥", label: "Nuevos clientes", description: "Adquisición" },
  { key: "reviews", emoji: "⭐", label: "Reviews / NPS", description: "Reputación y satisfacción" },
  { key: "reservations", emoji: "📅", label: "Reservas", description: "Mesas para una fecha específica" },
];

export interface UpdateObjectiveDTO {
  name?: string;
  emoji?: string;
  segmentId?: string;
  templateId?: string;
  setupMode?: ObjectiveSetupMode;
  cadence?: Partial<ObjectiveCadence>;
}

export interface ObjectiveSuggestion {
  preset: ObjectivePreset;
  suggestedName: string;
  suggestedSegmentPrompt: string;
  suggestedTemplatePrompt: string;
  suggestedCadence: ObjectiveCadence;
  rationale: string;
}

/* ─── Preset UI metadata ─── */

export interface PresetMeta {
  preset: ObjectivePreset;
  emoji: string;
  name: string;
  shortDescription: string;
  longDescription: string;
  exampleOutcome: string;
  glowColor: string;
  bgGradient: string;
  /** Mes principal (1-12) o null si es recurrente personal */
  triggerMonth: number | null;
  /** "fixed" si tiene fecha calendaria · "recurring" si es por evento personal */
  type: "fixed" | "recurring";
  /**
   * KPIs que la IA monitorea naturalmente al ejecutar este preset. Se muestran
   * en el picker (chips chiquitos) y en el dashboard de la campaña (bloque grande)
   * para que el usuario sepa qué esperar antes de activar.
   */
  trackedMetrics: CampaignTargetMetric[];
}

export const PRESETS: PresetMeta[] = [
  {
    preset: "valentines",
    emoji: "💝",
    name: "Día de los Enamorados",
    shortDescription: "Reservas y promos para parejas (14 feb)",
    longDescription:
      "Te ayudamos a llenar las mesas el 14 de febrero con promos para parejas, menús cerrados de 2 platos y reservas anticipadas. Activala 3-4 semanas antes para anticiparte a la demanda.",
    exampleOutcome: "Restaurants ven 2-3x más reservas esa semana.",
    glowColor: "rgb(236, 72, 153)",
    bgGradient:
      "linear-gradient(135deg, rgba(253, 242, 248, 1) 0%, rgba(252, 231, 243, 1) 100%)",
    triggerMonth: 2,
    type: "fixed",
    trackedMetrics: ["reservations", "revenue", "avg_ticket"],
  },
  {
    preset: "mothers_day",
    emoji: "👩",
    name: "Día de la Madre",
    shortDescription: "Almuerzo familiar del 3er domingo de octubre",
    longDescription:
      "Es el evento gastronómico más grande del año en Argentina. Las familias eligen restaurante 2-3 semanas antes. Te ayudamos a comunicar el menú especial y a tomar reservas.",
    exampleOutcome: "Reservas suben 4-6x vs un domingo normal.",
    glowColor: "rgb(244, 114, 182)",
    bgGradient:
      "linear-gradient(135deg, rgba(253, 244, 255, 1) 0%, rgba(250, 232, 255, 1) 100%)",
    triggerMonth: 10,
    type: "fixed",
    trackedMetrics: ["reservations", "revenue", "new_customers"],
  },
  {
    preset: "fathers_day",
    emoji: "👨",
    name: "Día del Padre",
    shortDescription: "Parrillada o cena especial (3er domingo de junio)",
    longDescription:
      "Promociones de cortes premium, tabla de fiambres, cervezas artesanales o asados pensados para juntar a la familia el 3er domingo de junio.",
    exampleOutcome: "Reservas familiares suben 2-3x vs domingo normal.",
    glowColor: "rgb(59, 130, 246)",
    bgGradient:
      "linear-gradient(135deg, rgba(239, 246, 255, 1) 0%, rgba(219, 234, 254, 1) 100%)",
    triggerMonth: 6,
    type: "fixed",
    trackedMetrics: ["reservations", "revenue"],
  },
  {
    preset: "friends_day",
    emoji: "🍻",
    name: "Día del Amigo",
    shortDescription: "Mesas grandes para reuniones (20 jul)",
    longDescription:
      "Grupos de 6+ personas, brindis, promos de tabla compartida. La noche del 20 de julio es uno de los días con más demanda de mesa grande del año.",
    exampleOutcome: "Mesas grandes (6+ pax) se duplican vs semana normal.",
    glowColor: "rgb(245, 158, 11)",
    bgGradient:
      "linear-gradient(135deg, rgba(255, 251, 235, 1) 0%, rgba(254, 243, 199, 1) 100%)",
    triggerMonth: 7,
    type: "fixed",
    trackedMetrics: ["reservations", "orders_count"],
  },
  {
    preset: "kids_day",
    emoji: "🧒",
    name: "Día del Niño",
    shortDescription: "Menú infantil + sorpresa (3er dom ago)",
    longDescription:
      "Familias buscan restaurantes kid-friendly el 3er domingo de agosto. Comunicá menú infantil, juegos, regalitos sorpresa, descuentos para grupos familiares.",
    exampleOutcome: "Ticket familiar promedio sube 30-40%.",
    glowColor: "rgb(168, 85, 247)",
    bgGradient:
      "linear-gradient(135deg, rgba(250, 245, 255, 1) 0%, rgba(243, 232, 255, 1) 100%)",
    triggerMonth: 8,
    type: "fixed",
    trackedMetrics: ["reservations", "avg_ticket", "new_customers"],
  },
  {
    preset: "workers_day",
    emoji: "🛠️",
    name: "Día del Trabajador",
    shortDescription: "Menú especial del 1 de mayo",
    longDescription:
      "Feriado nacional con almuerzos en familia. Activá una promo de menú del día, abierto en feriado, o tabla compartida para grupos.",
    exampleOutcome: "Mantenés flujo en un día que normalmente cae.",
    glowColor: "rgb(220, 38, 38)",
    bgGradient:
      "linear-gradient(135deg, rgba(254, 242, 242, 1) 0%, rgba(254, 226, 226, 1) 100%)",
    triggerMonth: 5,
    type: "fixed",
    trackedMetrics: ["orders_count", "revenue"],
  },
  {
    preset: "christmas_newyear",
    emoji: "🎄",
    name: "Navidad y Año Nuevo",
    shortDescription: "Cenas especiales y delivery festivo (22-dic a 1-ene)",
    longDescription:
      "Del 22 de diciembre al 1 de enero: reservas de cena navideña, pedidos pre-armados, delivery festivo, brindis. Los clientes deciden con 2-3 semanas de anticipación.",
    exampleOutcome: "Pedidos delivery suben 5-8x en la semana clave.",
    glowColor: "rgb(34, 197, 94)",
    bgGradient:
      "linear-gradient(135deg, rgba(240, 253, 244, 1) 0%, rgba(220, 252, 231, 1) 100%)",
    triggerMonth: 12,
    type: "fixed",
    trackedMetrics: ["revenue", "orders_count", "reservations"],
  },
  {
    preset: "birthday",
    emoji: "🎂",
    name: "Feliz cumpleaños",
    shortDescription: "Postre gratis + descuento, automático",
    longDescription:
      "Detección automática del cumpleaños del cliente. Le mandamos un saludo personal el día con un beneficio (postre gratis + 15% off) válido por 1 semana. Corre todo el año.",
    exampleOutcome: "60% de los clientes vuelve a visitar el restaurante.",
    glowColor: "rgb(217, 70, 239)",
    bgGradient:
      "linear-gradient(135deg, rgba(253, 244, 255, 1) 0%, rgba(250, 232, 255, 1) 100%)",
    triggerMonth: null,
    type: "recurring",
    trackedMetrics: ["retention", "revenue"],
  },
  {
    preset: "customer_anniversary",
    emoji: "🎉",
    name: "Aniversario del cliente",
    shortDescription: "Premiá fidelidad cada año (recurrente)",
    longDescription:
      "Detección automática del aniversario de la primera compra. 'Hace 1 año que sos cliente, gracias por elegirnos' + beneficio especial. Excelente para retención.",
    exampleOutcome: "Sube LTV de clientes recurrentes 20-30%.",
    glowColor: "rgb(20, 184, 166)",
    bgGradient:
      "linear-gradient(135deg, rgba(240, 253, 250, 1) 0%, rgba(204, 251, 241, 1) 100%)",
    triggerMonth: null,
    type: "recurring",
    trackedMetrics: ["retention", "purchase_frequency"],
  },
  // ───────────────────── Comportamentales ─────────────────────
  {
    preset: "cart_abandon",
    emoji: "🛒",
    name: "Carrito abandonado",
    shortDescription: "Recuperá ventas perdidas 2hs después",
    longDescription:
      "Detecta clientes que armaron un carrito en la web/app y no lo cerraron. Les manda un recordatorio amistoso 2 horas después con el resumen del pedido y un beneficio chico para destrabar la compra.",
    exampleOutcome: "Recupera entre 12% y 20% de los carritos abandonados.",
    glowColor: "rgb(239, 68, 68)",
    bgGradient:
      "linear-gradient(135deg, rgba(254, 242, 242, 1) 0%, rgba(254, 226, 226, 1) 100%)",
    triggerMonth: null,
    type: "recurring",
    trackedMetrics: ["orders_count", "revenue"],
  },
  {
    preset: "winback_vip",
    emoji: "🔁",
    name: "Reactivar clientes VIP",
    shortDescription: "Clientes alto LTV inactivos 30+ días",
    longDescription:
      "Identifica clientes con LTV en el top 20% que dejaron de pedir hace 30+ días y los reengancha con un beneficio premium personalizado. La IA estima cuánto descuento dar según su valor histórico.",
    exampleOutcome: "Reactiva 35-50% de los VIP sin sobrecostear el descuento.",
    glowColor: "rgb(168, 85, 247)",
    bgGradient:
      "linear-gradient(135deg, rgba(250, 245, 255, 1) 0%, rgba(243, 232, 255, 1) 100%)",
    triggerMonth: null,
    type: "recurring",
    trackedMetrics: ["retention", "revenue", "avg_ticket"],
  },
  {
    preset: "purchase_frequency",
    emoji: "📈",
    name: "Aumentar frecuencia",
    shortDescription: "Convertí 1-3 pedidos en recurrentes",
    longDescription:
      "Empuja a clientes que pidieron entre 1 y 3 veces a volver con una propuesta contextualizada (nuevo plato, ofertas relevantes a su historial). Sin ser invasivo.",
    exampleOutcome: "Sube la frecuencia promedio 18-25%.",
    glowColor: "rgb(59, 130, 246)",
    bgGradient:
      "linear-gradient(135deg, rgba(239, 246, 255, 1) 0%, rgba(219, 234, 254, 1) 100%)",
    triggerMonth: null,
    type: "recurring",
    trackedMetrics: ["purchase_frequency", "retention"],
  },
  {
    preset: "aov_increase",
    emoji: "💸",
    name: "Aumentar ticket promedio",
    shortDescription: "Combos a clientes con ticket bajo",
    longDescription:
      "Detecta clientes cuyo ticket promedio está debajo del promedio de la marca y les sugiere combos / upsells (postre + bebida, segundo plato, etc) la próxima vez que pidan.",
    exampleOutcome: "Incrementa AOV entre 8% y 15%.",
    glowColor: "rgb(34, 197, 94)",
    bgGradient:
      "linear-gradient(135deg, rgba(240, 253, 244, 1) 0%, rgba(220, 252, 231, 1) 100%)",
    triggerMonth: null,
    type: "recurring",
    trackedMetrics: ["avg_ticket", "revenue"],
  },
  {
    preset: "welcome_first",
    emoji: "👋",
    name: "Bienvenida primera compra",
    shortDescription: "Saludá al cliente apenas pide",
    longDescription:
      "Después de la primera compra exitosa, le mandamos un mensaje cálido agradeciendo + tip para próximo pedido (10% off, envío gratis). Importante para fidelizar antes del segundo pedido.",
    exampleOutcome: "67% repite pedido en los 14 días siguientes.",
    glowColor: "rgb(249, 115, 22)",
    bgGradient:
      "linear-gradient(135deg, rgba(255, 247, 237, 1) 0%, rgba(254, 215, 170, 1) 100%)",
    triggerMonth: null,
    type: "recurring",
    trackedMetrics: ["retention", "purchase_frequency"],
  },
  {
    preset: "winback_general",
    emoji: "😴",
    name: "Despertar inactivos",
    shortDescription: "Clientes sin pedir 60+ días",
    longDescription:
      "Para clientes que ya no son VIP pero ya fueron tuyos. Los activamos con un descuento más agresivo (20-30%) y un mensaje honesto: 'hace tiempo que no nos vemos'. Si no convierten en 2 intentos, los archivamos.",
    exampleOutcome: "Reactiva 15-22% de la base dormida.",
    glowColor: "rgb(100, 116, 139)",
    bgGradient:
      "linear-gradient(135deg, rgba(248, 250, 252, 1) 0%, rgba(241, 245, 249, 1) 100%)",
    triggerMonth: null,
    type: "recurring",
    trackedMetrics: ["retention", "orders_count"],
  },
  {
    preset: "cross_sell",
    emoji: "🛍️",
    name: "Cross-sell post-pedido",
    shortDescription: "Sugerí complementos 3 días después",
    longDescription:
      "3 días después de un pedido, le sugerimos al cliente productos que combinan bien con lo que pidió. Si pidió pizza, ofrecemos postre. Si pidió milanesas, ofrecemos picada. AI elige qué proponer.",
    exampleOutcome: "Sube revenue por cliente 12-18%.",
    glowColor: "rgb(217, 70, 239)",
    bgGradient:
      "linear-gradient(135deg, rgba(253, 244, 255, 1) 0%, rgba(250, 232, 255, 1) 100%)",
    triggerMonth: null,
    type: "recurring",
    trackedMetrics: ["avg_ticket", "revenue"],
  },
  {
    preset: "reorder_reminder",
    emoji: "⏰",
    name: "Próximo pedido estimado",
    shortDescription: "IA predice cuándo va a pedir de nuevo",
    longDescription:
      "La IA aprende el patrón de pedido del cliente (cada 10 días, cada viernes, etc) y le manda un recordatorio justo antes de su próxima ventana esperada. Subliminal pero efectivo.",
    exampleOutcome: "Reduce el churn de clientes activos 25%.",
    glowColor: "rgb(14, 165, 233)",
    bgGradient:
      "linear-gradient(135deg, rgba(240, 249, 255, 1) 0%, rgba(186, 230, 253, 1) 100%)",
    triggerMonth: null,
    type: "recurring",
    trackedMetrics: ["purchase_frequency", "retention"],
  },
  {
    preset: "review_request",
    emoji: "⭐",
    name: "Pedir review",
    shortDescription: "Review en Google 2 días post-entrega",
    longDescription:
      "2 días después de cada pedido entregado, pedimos amablemente una review en Google Maps o nuestra plataforma. Solo a quienes tuvieron experiencia OK (no a quejas). Sube tu rating sin esfuerzo manual.",
    exampleOutcome: "+18% en cantidad de reviews mensuales con rating 4+.",
    glowColor: "rgb(234, 179, 8)",
    bgGradient:
      "linear-gradient(135deg, rgba(254, 252, 232, 1) 0%, rgba(254, 240, 138, 1) 100%)",
    triggerMonth: null,
    type: "recurring",
    trackedMetrics: ["reviews"],
  },
  // ───────────────────── Custom (no aparece en el grid) ─────────────────────
  {
    preset: "custom",
    emoji: "🎯",
    name: "Campaña personalizada",
    shortDescription: "Armala desde cero, sin blueprint",
    longDescription:
      "Sin preset. Definís vos mismo el objetivo, la audiencia, la plantilla y la cadencia. Ideal para experimentos, campañas únicas o cuando ninguno de los 18 presets encaja.",
    exampleOutcome: "Flexibilidad total — solo guiada por tus reglas de marca.",
    glowColor: "rgb(100, 116, 139)",
    bgGradient:
      "linear-gradient(135deg, rgba(248, 250, 252, 1) 0%, rgba(241, 245, 249, 1) 100%)",
    triggerMonth: null,
    type: "recurring",
    trackedMetrics: [], // Las elige el usuario en el form
  },
];

export const PRESET_BY_KEY: Record<ObjectivePreset, PresetMeta> =
  PRESETS.reduce(
    (acc, p) => {
      acc[p.preset] = p;
      return acc;
    },
    {} as Record<ObjectivePreset, PresetMeta>,
  );
